import type {
  GSplatShaderEffectConfig,
  GSplatShaderEffectLanguage,
  GSplatShaderEffectScope,
  GSplatShaderEffectStage,
  GSplatShaderEffectUniforms,
} from '@/plugins/gsplat/effects/shaderEffectTypes'
import type { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'

export interface GSplatShaderEffectOptions {
  id?: string
  material?: GaussianSplatMaterial | null
  enabled?: boolean
  uniforms?: GSplatShaderEffectUniforms
  defines?: GSplatShaderEffectConfig['defines']
  scope?: GSplatShaderEffectScope
}

/**
 * PlayCanvas `GsplatShaderEffect` 的 Share3DEngine 运行时基类。
 *
 * 它不依赖 PlayCanvas Script 系统，只保留 shader 注入、启停、计时、uniform 更新和清理职责。
 */
export abstract class GSplatShaderEffect {
  readonly id?: string
  material: GaussianSplatMaterial | null
  effectTime = 0

  protected readonly uniforms: GSplatShaderEffectUniforms
  protected readonly defines?: GSplatShaderEffectConfig['defines']
  protected readonly scope: GSplatShaderEffectScope

  private _enabled = false
  private _destroyed = false
  private _shadersNeedApplication = false
  private _waitingForMaterial = false
  private _activeConfig: GSplatShaderEffectConfig | null = null

  constructor(options: GSplatShaderEffectOptions = {}) {
    this.id = options.id
    this.material = options.material ?? null
    this.uniforms = { ...(options.uniforms ?? {}) }
    this.defines = options.defines
    this.scope = options.scope ?? 'material'
    this._enabled = options.enabled ?? true
    this._shadersNeedApplication = this._enabled
    this._waitingForMaterial = this.material === null
  }

  get enabled(): boolean {
    return this._enabled
  }

  set enabled(value: boolean) {
    if (value) {
      this.enable()
    } else {
      this.disable()
    }
  }

  get destroyed(): boolean {
    return this._destroyed
  }

  setMaterial(material: GaussianSplatMaterial | null): boolean {
    if (this._destroyed || this.material === material) {
      if (this._enabled && this.material && this._shadersNeedApplication) {
        return this.applyShaders()
      }
      return false
    }

    this.removeShaders()
    this.material = material

    if (this._enabled) {
      return this.applyShaders()
    }
    return false
  }

  enable(material?: GaussianSplatMaterial | null): boolean {
    if (this._destroyed) {
      return false
    }

    const wasEnabled = this._enabled
    let materialApplied = false
    if (material !== undefined) {
      materialApplied = this.setMaterial(material)
    }

    this._enabled = true
    // PlayCanvas resets elapsed time on every enable, including re-enabling an already
    // enabled effect after a pending/material replacement transaction.
    this.effectTime = 0

    if (wasEnabled && materialApplied) {
      return true
    }
    return this.applyShaders()
  }

  disable(): boolean {
    if (this._destroyed) {
      return false
    }

    this._enabled = false
    this._shadersNeedApplication = false
    return this.removeShaders()
  }

  restart(): boolean {
    if (this._destroyed) {
      return false
    }

    this.effectTime = 0
    this._enabled = true
    return this.applyShaders()
  }

  applyShaders(material?: GaussianSplatMaterial | null): boolean {
    if (this._destroyed) {
      return false
    }

    if (material !== undefined) {
      if (this.material !== material) {
        this.removeShaders()
        this.material = material
      }
    }

    const materialRef = this.material
    if (!materialRef) {
      this._shadersNeedApplication = true
      this._waitingForMaterial = true
      return false
    }

    const config = this.createShaderEffectConfig()
    materialRef.setShaderEffect(config)
    this._activeConfig = config
    this._shadersNeedApplication = false
    this._waitingForMaterial = false
    this.updateEffect(this.effectTime, 0)
    return true
  }

  removeShaders(): boolean {
    if (!this.material) {
      this._activeConfig = null
      return false
    }

    if (this._activeConfig) {
      this.material.clearShaderEffect(this._activeConfig)
    }
    this._activeConfig = null
    return true
  }

  update(dt: number): void {
    if (this._destroyed || !this._enabled) {
      return
    }

    if (!this.material) {
      this._shadersNeedApplication = true
      return
    }

    if (this._shadersNeedApplication) {
      const wasWaitingForMaterial = this._waitingForMaterial
      if (!this.applyShaders()) {
        return
      }
      if (!this.material) {
        return
      }
      // A late component/material resolution consumes this frame. The next frame starts
      // elapsed-time advancement, matching the upstream initialize/apply/update ordering.
      if (wasWaitingForMaterial) return
    }

    const delta = Number.isFinite(dt) ? Math.max(0, dt) : 0
    if (delta > 0) {
      this.effectTime += delta
    }
    this.updateEffect(this.effectTime, delta)
  }

  setElapsedTime(seconds: number): void {
    if (!Number.isFinite(seconds) || seconds < 0) {
      throw new Error('GSplat shader effect elapsed time must be a non-negative finite number.')
    }
    if (this._destroyed) {
      return
    }

    this.effectTime = seconds
    this.syncUniforms()
  }

  syncUniforms(): void {
    if (this._destroyed || !this._enabled) {
      return
    }

    if (!this.material) {
      this._shadersNeedApplication = true
      return
    }

    if (this._shadersNeedApplication) {
      this.applyShaders()
      return
    }

    this.updateEffect(this.effectTime, 0)
  }

  setUniform(name: string, value: unknown): boolean {
    const uniform = this.uniforms[name]
    if (uniform) {
      uniform.value = value
    } else {
      this.uniforms[name] = { value }
    }

    return this.material?.setShaderEffectUniform(name, value) ?? false
  }

  /** 标记效果已完成但保留当前 shader，由效果自身通过 uniform 旁路完成帧编译。 */
  protected finishWithShadersAttached(): void {
    if (this._destroyed) {
      return
    }

    if (this.material && this._activeConfig) {
      this.material.deactivateShaderEffect(this._activeConfig)
    }
    this._enabled = false
    this._shadersNeedApplication = false
  }

  destroy(): void {
    if (this._destroyed) {
      return
    }

    this.disable()
    this._destroyed = true
    this.onDestroy()
  }

  protected createShaderEffectConfig(): GSplatShaderEffectConfig {
    const vertexGLSL = this.getShaderGLSL()
    const vertexWGSL = this.getShaderWGSL()
    const fragmentGLSL = this.getFragmentShaderGLSL()
    const fragmentWGSL = this.getFragmentShaderWGSL()

    return {
      id: this.id,
      // `glsl` remains the public vertex GLSL compatibility field.
      glsl: vertexGLSL,
      wgsl: vertexWGSL,
      vertexGLSL,
      vertexWGSL,
      fragmentGLSL,
      fragmentWGSL,
      uniforms: this.uniforms,
      defines: this.defines,
      scope: this.scope,
    }
  }

  /**
   * 返回 vertex GLSL hook。基类没有自定义 hook 时返回 null，保留默认 chunk。
   */
  getShaderGLSL(): string | null {
    return null
  }

  /**
   * 返回 vertex WGSL hook。
   *
   * WGSL 与 GLSL 是两个独立语言边界；基类不得把 GLSL 当作 WGSL 返回。
   */
  getShaderWGSL(): string | null {
    return null
  }

  /** 返回可选 fragment GLSL hook；基类默认不覆盖 fragment chunk。 */
  getFragmentShaderGLSL(): string | null {
    return null
  }

  /** 返回可选 fragment WGSL hook；基类默认不覆盖 fragment chunk。 */
  getFragmentShaderWGSL(): string | null {
    return null
  }

  /**
   * 按 backend language/stage 选择 effect source。
   *
   * 该窄 helper 只负责选择，不执行 material/GPU mutation；调用方应在提交前完成
   * source 存在性校验与事务 staging。
   */
  getShaderSource(
    language: GSplatShaderEffectLanguage,
    stage: GSplatShaderEffectStage
  ): string | null {
    if (stage === 'fragment') {
      return language === 'wgsl' ? this.getFragmentShaderWGSL() : this.getFragmentShaderGLSL()
    }
    return language === 'wgsl' ? this.getShaderWGSL() : this.getShaderGLSL()
  }

  protected updateEffect(_effectTime: number, _dt: number): void {
    // 子类按需覆盖。
  }

  protected onDestroy(): void {
    // 子类按需释放自身持有的贴图或缓存。
  }
}
